import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import api from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Mail, Loader2, Clock, FileText } from "lucide-react";

const INITIAL = { name: "", email: "", subject: "", message: "" };

const inputClasses =
    "w-full bg-white border border-stone-200 focus:border-wine/60 rounded-sm px-4 py-3.5 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-colors duration-300";

export default function Contact() {
    const [form, setForm] = useState(INITIAL);
    const [sending, setSending] = useState(false);

    const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/contact", form);
            toast.success("Enquiry sent", {
                description: "Thank you — your message has been sent to Grayhaven Research. We will respond as soon as possible.",
            });
            setForm(INITIAL);
        } catch {
            toast.error("Something went wrong", {
                description: "Your message could not be sent. Please email hello@grayhavenresearch.com directly.",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <PageHeader
                eyebrow="Get In Touch"
                title="Contact Grayhaven"
                description="Questions about materials, testing, or documentation — we welcome them. Transparency starts with conversation."
            />

            <section className="pb-24 sm:pb-32 px-6 lg:px-8">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <Reveal className="lg:col-span-5">
                        <div className="space-y-10">
                            <div>
                                <p className="eyebrow">Email</p>
                                <a
                                    href="mailto:hello@grayhavenresearch.com"
                                    data-testid="contact-email-link"
                                    className="mt-4 inline-flex items-center gap-3 font-serif text-2xl font-light text-brand hover:text-wine transition-colors duration-300"
                                >
                                    <Mail className="h-5 w-5 text-gold" />
                                    hello@grayhavenresearch.com
                                </a>
                            </div>
                            <div className="flex items-start gap-4 border-t border-stone-200 pt-8">
                                <Clock className="h-5 w-5 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
                                <p className="text-sm leading-relaxed text-mist">
                                    Enquiries are reviewed carefully and answered as promptly as possible. For
                                    documentation requests, please include the material name and, where applicable,
                                    the batch or lot number.
                                </p>
                            </div>
                            <div className="flex items-start gap-4 border-t border-stone-200 pt-8">
                                <FileText className="h-5 w-5 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
                                <p className="text-sm leading-relaxed text-mist">
                                    Grayhaven materials are supplied strictly for laboratory research use only. We are
                                    unable to provide medical advice, dosing guidance, or information relating to human
                                    or veterinary use.
                                </p>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.15} className="lg:col-span-7">
                        <form
                            onSubmit={handleSubmit}
                            data-testid="contact-form"
                            className="bg-white border border-stone-200 rounded-sm p-8 sm:p-10"
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="contact-name" className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-400 mb-3">
                                        Name
                                    </label>
                                    <input
                                        id="contact-name"
                                        type="text"
                                        required
                                        maxLength={200}
                                        value={form.name}
                                        onChange={update("name")}
                                        placeholder="Your name"
                                        data-testid="contact-name-input"
                                        className={inputClasses}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contact-email" className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-400 mb-3">
                                        Email
                                    </label>
                                    <input
                                        id="contact-email"
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={update("email")}
                                        placeholder="you@example.com"
                                        data-testid="contact-email-input"
                                        className={inputClasses}
                                    />
                                </div>
                            </div>
                            <div className="mt-6">
                                <label htmlFor="contact-subject" className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-400 mb-3">
                                    Subject
                                </label>
                                <input
                                    id="contact-subject"
                                    type="text"
                                    required
                                    maxLength={300}
                                    value={form.subject}
                                    onChange={update("subject")}
                                    placeholder="How can we help?"
                                    data-testid="contact-subject-input"
                                    className={inputClasses}
                                />
                            </div>
                            <div className="mt-6">
                                <label htmlFor="contact-message" className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-400 mb-3">
                                    Message
                                </label>
                                <textarea
                                    id="contact-message"
                                    required
                                    maxLength={5000}
                                    rows={6}
                                    value={form.message}
                                    onChange={update("message")}
                                    placeholder="Write your message…"
                                    data-testid="contact-message-input"
                                    className={`${inputClasses} resize-y`}
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={sending}
                                data-testid="contact-submit-btn"
                                className="mt-8 inline-flex items-center justify-center gap-2 bg-wine text-white px-10 py-3.5 uppercase tracking-[0.2em] text-xs font-semibold hover:bg-wine-deep disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
                            >
                                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                                {sending ? "Sending…" : "Send Message"}
                            </button>
                        </form>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
